import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CommsProvider, UUID } from '@4zen/api-client';
import { api } from '@/lib/api';

export interface ConversationFilters {
  provider?: CommsProvider;
  client_id?: UUID;
  unread_only?: boolean;
  mine_only?: boolean;
}

export function useConversations(filters: ConversationFilters = {}) {
  return useQuery({
    queryKey: ['conversations', filters],
    queryFn: () => api.comms.conversations(filters),
    placeholderData: (previous) => previous,
    // The realtime bus invalidates on new mail; this is just a safety net.
    refetchInterval: 60_000,
  });
}

export function useConversation(conversationId?: UUID) {
  return useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () => api.comms.conversation(conversationId!),
    enabled: Boolean(conversationId),
  });
}

export function useUnreadCounters() {
  return useQuery({ queryKey: ['comms-unread'], queryFn: () => api.comms.unread() });
}

export function useSendMessage(conversationId?: UUID) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => api.comms.send(conversationId!, { body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

export function useMarkRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: UUID) => api.comms.markRead(conversationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['comms-unread'] });
    },
  });
}

export function useCalls(filters: { client_id?: UUID; missed_only?: boolean } = {}) {
  return useQuery({
    queryKey: ['calls', filters],
    queryFn: () => api.comms.calls(filters),
  });
}

export function useChannelAccounts() {
  return useQuery({ queryKey: ['channel-accounts'], queryFn: () => api.comms.accounts() });
}

export function useDial() {
  return useMutation({
    mutationFn: ({ accountId, number }: { accountId: UUID; number: string }) =>
      api.comms.dial({ account_id: accountId, number }),
  });
}
